import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
  Avatar,
  Badge,
  Switch,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  MenuItem,
  Select,
  Divider,
} from "@mui/material";
import {
  Send as SendIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Close as CloseIcon,
  SmartToy as AIIcon,
  MarkEmailUnreadOutlined as MarkUnreadIcon,
  PersonOutline as PersonIcon,
} from "@mui/icons-material";
import {
  ConversationMetadataUpdate,
  ConversationPriority,
  ConversationStatus,
  getConversations,
  getConversationMessages,
  sendMessage,
  updateConversationMetadata,
} from "../../services/whatsapp.service";
import {
  getUserSettingsById,
  updateAgentStatus,
} from "../../services/settings.service";
import useAuthUser from "../hooks/useAuthUser";
import { BusinessSetting } from "../../models/settings.model";
import { AgentDetailModel } from "../../models/agent.model";
import { useDisclosure } from "@mantine/hooks";
import { useSearchParams } from "react-router-dom";

interface ChatMessage {
  id: string;
  text: string; // For preview/lastMessage
  segments?: MessageSegment[]; // For detailed rendering with images
  sender: "user" | "agent";
  timestamp: Date;
  isMultiSegment?: boolean; // Flag to indicate this message came from multiple JSON objects
}

interface MessageSegment {
  type: "text" | "image";
  text?: string;
  imageUrl?: string;
  imageCaption?: string;
}

interface ChatConversation {
  id: string;
  name: string;
  avatar?: string;
  phoneNumber?: string;
  lastMessage: string;
  lastMessageTime: Date;
  unreadCount: number;
  crmStatus: ConversationStatus;
  priority: ConversationPriority;
  assignedTo?: string;
  labels: string[];
  notes?: string;
  markedUnread: boolean;
  messages: ChatMessage[];
}

const formatTime = (date: Date) => {
  const now = new Date();
  const diff = now.getTime() - date.getTime();

  // Less than a minute
  if (diff < 60000) return "Just now";

  // Less than an hour
  if (diff < 3600000) {
    const minutes = Math.floor(diff / 60000);
    return `${minutes}m ago`;
  }

  // Less than a day
  if (diff < 86400000) {
    const hours = Math.floor(diff / 3600000);
    return `${hours}h ago`;
  }

  // Format as date
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

/**
 * Parse concatenated JSON objects into individual messages
 * Each {message: [...]} becomes its own message
 */
const parseConcatenatedMessages = (content: string): ChatMessage[] | null => {
  // Try to extract individual JSON objects
  const jsonMatches = content.match(/{[\s\S]*?}(?=\s*{|$)/g);
  if (!jsonMatches || jsonMatches.length === 0) return null;

  const messages: ChatMessage[] = [];

  jsonMatches.forEach((jsonStr, index) => {
    try {
      const obj = JSON.parse(jsonStr);
      if (obj && Array.isArray(obj.message)) {
        // Each JSON object becomes its own message
        const segments: MessageSegment[] = [];
        let textContent = "";

        obj.message.forEach((segment: any) => {
          if (segment.type === "text" && segment.text?.body) {
            segments.push({
              type: "text",
              text: segment.text.body,
            });
            textContent += segment.text.body;
          } else if (segment.type === "image" && segment.image) {
            segments.push({
              type: "image",
              imageUrl: segment.image.link,
              imageCaption: segment.image.caption,
            });
            if (segment.image.caption) {
              textContent += segment.image.caption;
            }
          }
        });

        if (segments.length > 0) {
          messages.push({
            id: `msg_${Date.now()}_${index}`,
            text: textContent || "[Image]",
            segments,
            sender: "agent",
            timestamp: new Date(),
            isMultiSegment: true,
          });
        }
      }
    } catch (parseErr) {
      console.error("Error parsing individual JSON object:", parseErr);
    }
  });

  return messages.length > 0 ? messages : null;
};

/**
 * Parse message content and extract segments (text and images)
 */
const parseMessageSegments = (content: string | any): MessageSegment[] => {
  // If it's a string, try to parse as JSON first
  if (typeof content === "string") {
    // First check if it looks like JSON (starts with {)
    if (content.trim().startsWith("{")) {
      try {
        const parsed = JSON.parse(content);

        // Handle case 1: {message: [{type, text/image}]}
        if (
          parsed &&
          typeof parsed === "object" &&
          Array.isArray(parsed.message)
        ) {
          const segments: MessageSegment[] = [];
          parsed.message.forEach((segment: any) => {
            if (segment.type === "text" && segment.text?.body) {
              segments.push({
                type: "text",
                text: segment.text.body,
              });
            } else if (segment.type === "image" && segment.image) {
              segments.push({
                type: "image",
                imageUrl: segment.image.link,
                imageCaption: segment.image.caption,
              });
            }
          });
          return segments.length > 0
            ? segments
            : [{ type: "text", text: "Unable to parse message" }];
        }
      } catch (e) {
        // If JSON parsing fails, it might be multiple JSON objects concatenated
        // Try to extract individual JSON objects
        const jsonMatches = content.match(/{[\s\S]*?}(?=\s*{|$)/g);
        if (jsonMatches && jsonMatches.length > 0) {
          const segments: MessageSegment[] = [];

          jsonMatches.forEach((jsonStr) => {
            try {
              const obj = JSON.parse(jsonStr);
              if (obj && Array.isArray(obj.message)) {
                obj.message.forEach((segment: any) => {
                  if (segment.type === "text" && segment.text?.body) {
                    segments.push({
                      type: "text",
                      text: segment.text.body,
                    });
                  } else if (segment.type === "image" && segment.image) {
                    segments.push({
                      type: "image",
                      imageUrl: segment.image.link,
                      imageCaption: segment.image.caption,
                    });
                  }
                });
              }
            } catch (parseErr) {
              console.error("Error parsing individual JSON object:", parseErr);
            }
          });

          if (segments.length > 0) {
            return segments;
          }
        }
      }
    }

    // If all JSON parsing fails or it's plain text, return the string as-is
    return [{ type: "text", text: content }];
  }

  // If it's already an object and has message array
  if (
    content &&
    typeof content === "object" &&
    Array.isArray(content.message)
  ) {
    const segments: MessageSegment[] = [];
    content.message.forEach((segment: any) => {
      if (segment.type === "text" && segment.text?.body) {
        segments.push({
          type: "text",
          text: segment.text.body,
        });
      } else if (segment.type === "image" && segment.image) {
        segments.push({
          type: "image",
          imageUrl: segment.image.link,
          imageCaption: segment.image.caption,
        });
      }
    });
    return segments.length > 0
      ? segments
      : [{ type: "text", text: "Unable to parse message" }];
  }

  // Fallback
  return [{ type: "text", text: "Unable to parse message" }];
};

/**
 * Extract text content from both string and WhatsappMessage formats (for lastMessage preview)
 */
const extractTextFromContent = (content: string | any): string => {
  // If it's a string, try to parse as JSON first (only if it looks like JSON)
  if (typeof content === "string") {
    // First check if it looks like JSON (starts with {)
    if (content.trim().startsWith("{")) {
      try {
        const parsed = JSON.parse(content);

        // If it parses successfully and has message array, process it
        if (
          parsed &&
          typeof parsed === "object" &&
          Array.isArray(parsed.message)
        ) {
          const result = parsed.message
            .map((segment: any) => {
              if (segment.type === "text" && segment.text?.body) {
                return segment.text.body;
              } else if (segment.type === "image" && segment.image?.caption) {
                return segment.image.caption;
              } else if (segment.type === "image" && segment.image?.link) {
                return `[Image]`;
              }
              return "";
            })
            .filter((text: string) => text.length > 0)
            .join("\n");
          return result;
        }
      } catch (e) {
        // If JSON parsing fails, it might be multiple JSON objects concatenated
        // Try to extract individual JSON objects
        const jsonMatches = content.match(/{[\s\S]*?}(?=\s*{|$)/g);
        if (jsonMatches && jsonMatches.length > 0) {
          const textParts: string[] = [];

          jsonMatches.forEach((jsonStr) => {
            try {
              const obj = JSON.parse(jsonStr);
              if (obj && Array.isArray(obj.message)) {
                obj.message.forEach((segment: any) => {
                  if (segment.type === "text" && segment.text?.body) {
                    textParts.push(segment.text.body);
                  } else if (
                    segment.type === "image" &&
                    segment.image?.caption
                  ) {
                    textParts.push(segment.image.caption);
                  }
                });
              }
            } catch (parseErr) {
              console.error("Error parsing individual JSON object:", parseErr);
            }
          });

          if (textParts.length > 0) {
            const result = textParts.join("\n");
            return result;
          }
        }
      }
    }

    // If not JSON or parsing failed, return as-is (plain text message)
    return content;
  }

  // If it's already an object and has message array
  if (
    content &&
    typeof content === "object" &&
    Array.isArray(content.message)
  ) {
    console.log("Processing as object with message array");
    return content.message
      .map((segment: any) => {
        if (segment.type === "text" && segment.text?.body) {
          return segment.text.body;
        } else if (segment.type === "image" && segment.image?.caption) {
          return segment.image.caption;
        } else if (segment.type === "image" && segment.image?.link) {
          return `[Image]`;
        }
        return "";
      })
      .filter((text: string) => text.length > 0)
      .join("\n");
  }

  // Fallback
  console.log("No recognized format, returning fallback");
  return "Unable to parse message";
};

const getStatusColor = (status: ConversationStatus) => {
  switch (status) {
    case "OPEN":
      return "#16845b";
    case "PENDING":
      return "#b7750a";
    case "RESOLVED":
      return "#65766f";
    default:
      return "#65766f";
  }
};

type InboxFilter = "ALL" | "UNREAD" | ConversationStatus;

interface ChatListProps {
  conversations: ChatConversation[];
  selectedChat: ChatConversation | null;
  onSelectChat: (chat: ChatConversation) => void;
  searchTerm: string;
  onSearchChange: (term: string) => void;
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  totalConversations: number;
  filter: InboxFilter;
  onFilterChange: (filter: InboxFilter) => void;
  onLoadMore: () => void;
}

const ChatList: React.FC<ChatListProps> = ({
  conversations,
  selectedChat,
  onSelectChat,
  searchTerm,
  onSearchChange,
  isLoading,
  isLoadingMore,
  hasMore,
  totalConversations,
  filter,
  onFilterChange,
  onLoadMore,
}) => {
  const filteredChats = conversations;

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRight: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.paper",
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight={700}>
            Inbox
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {totalConversations} conversations
          </Typography>
        </Stack>
        <TextField
          fullWidth
          size="small"
          placeholder="Search conversations..."
          inputProps={{ "aria-label": "Search conversations" }}
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: "action.active" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
            },
          }}
        />
        <Stack
          direction="row"
          spacing={0.75}
          sx={{ mt: 1.5, overflowX: "auto", pb: 0.5 }}
        >
          {(["ALL", "UNREAD", "OPEN", "PENDING", "RESOLVED"] as InboxFilter[]).map(
            (item) => (
              <Chip
                key={item}
                label={item.charAt(0) + item.slice(1).toLowerCase()}
                size="small"
                color={filter === item ? "primary" : "default"}
                variant={filter === item ? "filled" : "outlined"}
                onClick={() => onFilterChange(item)}
              />
            ),
          )}
        </Stack>
      </Box>

      {/* Chat List */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "action.disabled",
            borderRadius: "4px",
          },
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            <CircularProgress size={40} />
          </Box>
        ) : filteredChats.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              color: "text.secondary",
            }}
          >
            <Typography variant="body2">No conversations found</Typography>
          </Box>
        ) : (
          filteredChats.map((chat) => (
            <Box
              key={chat.id}
              onClick={() => onSelectChat(chat)}
              sx={{
                p: 1.5,
                cursor: "pointer",
                borderBottom: "1px solid",
                borderColor: "divider",
                backgroundColor:
                  selectedChat?.id === chat.id
                    ? "action.selected"
                    : "transparent",
                transition: "background-color 0.2s",
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                {/* Avatar */}
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  variant="dot"
                  sx={{
                    "& .MuiBadge-badge": {
                      backgroundColor: getStatusColor(chat.crmStatus),
                      boxShadow: `0 0 0 2px ${getStatusColor(chat.crmStatus)}`,
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: "action.selected",
                      color: "primary.main",
                      fontSize: "1.2rem",
                    }}
                  >
                    {chat.name.charAt(0)}
                  </Avatar>
                </Badge>

                {/* Chat Info */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 0.5 }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      noWrap
                      sx={{
                        color:
                          chat.unreadCount > 0
                            ? "text.primary"
                            : "text.secondary",
                      }}
                    >
                      {chat.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        flexShrink: 0,
                        ml: 1,
                      }}
                    >
                      {formatTime(chat.lastMessageTime)}
                    </Typography>
                  </Stack>

                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography
                      variant="caption"
                      noWrap
                      sx={{
                        color: "text.secondary",
                        flex: 1,
                      }}
                    >
                      {chat.lastMessage}
                    </Typography>
                    {chat.unreadCount > 0 && (
                      <Chip
                        label={chat.unreadCount}
                        size="small"
                        color="primary"
                        sx={{ ml: 1, flexShrink: 0 }}
                      />
                    )}
                  </Stack>
                  <Stack direction="row" spacing={0.5} sx={{ mt: 0.75 }}>
                    <Chip
                      label={chat.crmStatus.toLowerCase()}
                      size="small"
                      variant="outlined"
                      sx={{ height: 20, fontSize: "0.65rem" }}
                    />
                    {chat.priority !== "NORMAL" && (
                      <Chip
                        label={chat.priority.toLowerCase()}
                        size="small"
                        color={chat.priority === "URGENT" ? "error" : "warning"}
                        sx={{ height: 20, fontSize: "0.65rem" }}
                      />
                    )}
                  </Stack>
                </Box>
              </Stack>
            </Box>
          ))
        )}
        {!isLoading && hasMore && (
          <Box sx={{ p: 2, textAlign: "center" }}>
            <Button
              size="small"
              variant="outlined"
              onClick={onLoadMore}
              disabled={isLoadingMore}
            >
              {isLoadingMore ? "Loading..." : "Load more conversations"}
            </Button>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

interface ChatPanelProps {
  isSendingMessage: boolean;
  chat: ChatConversation | null;
  onSendMessage: (message: string) => void;
  messages: ChatMessage[];
  isLoadingMessages: boolean;
  isLoadingOlder: boolean;
  hasOlderMessages: boolean;
  onLoadOlder: () => void;
  onUpdateMetadata: (update: ConversationMetadataUpdate) => Promise<void>;
}

const ChatPanel: React.FC<ChatPanelProps> = ({
  isSendingMessage,
  chat,
  onSendMessage,
  messages,
  isLoadingMessages,
  isLoadingOlder,
  hasOlderMessages,
  onLoadOlder,
  onUpdateMetadata,
}) => {
  const [messageInput, setMessageInput] = useState("");
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [assignedTo, setAssignedTo] = useState("");
  const [labels, setLabels] = useState("");
  const [notes, setNotes] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setAssignedTo(chat?.assignedTo || "");
    setLabels(chat?.labels.join(", ") || "");
    setNotes(chat?.notes || "");
  }, [chat?.phoneNumber, chat?.assignedTo, chat?.labels, chat?.notes]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = () => {
    if (messageInput.trim()) {
      onSendMessage(messageInput);
      setMessageInput("");
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!chat) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "background.default",
        }}
      >
        <Stack alignItems="center" spacing={2}>
          <Typography variant="h6" sx={{ color: "text.secondary" }}>
            Select a conversation to start messaging
          </Typography>
          <Typography variant="body2" sx={{ color: "text.disabled" }}>
            Choose from your conversations on the left
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: "background.paper",
      }}
    >
      {/* Chat Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ flex: 1, minWidth: 0 }}
        >
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            variant="dot"
            sx={{
              "& .MuiBadge-badge": {
                backgroundColor: getStatusColor(chat.crmStatus),
                boxShadow: `0 0 0 2px ${getStatusColor(chat.crmStatus)}`,
              },
            }}
          >
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: "primary.main",
              }}
            >
              {chat.name.charAt(0)}
            </Avatar>
          </Badge>
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              {chat.name}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {chat.phoneNumber}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <Select
              value={chat.crmStatus}
              inputProps={{ "aria-label": "Conversation status" }}
              onChange={(event) =>
                onUpdateMetadata({ status: event.target.value as ConversationStatus })
              }
            >
              <MenuItem value="OPEN">Open</MenuItem>
              <MenuItem value="PENDING">Pending</MenuItem>
              <MenuItem value="RESOLVED">Resolved</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title={chat.markedUnread ? "Marked unread" : "Mark as unread"}>
            <IconButton
              size="small"
              color={chat.markedUnread ? "primary" : "default"}
              onClick={() => onUpdateMetadata({ markedUnread: !chat.markedUnread })}
            >
              <MarkUnreadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="CRM details">
            <IconButton size="small" onClick={() => setDetailsOpen(true)}>
              <MoreVertIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* Messages Area */}
      {isLoadingMessages ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "background.default",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <CircularProgress size={50} />
          <Typography variant="body2" color="text.secondary">
            Loading conversation...
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            p: 2,
            backgroundColor: "background.default",
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            position: "relative",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "action.disabled",
              borderRadius: "4px",
            },
          }}
        >
          {hasOlderMessages && (
            <Box sx={{ textAlign: "center", mb: 1 }}>
              <Button
                size="small"
                variant="text"
                onClick={onLoadOlder}
                disabled={isLoadingOlder}
              >
                {isLoadingOlder ? "Loading older messages..." : "Load older messages"}
              </Button>
            </Box>
          )}
          {messages.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                color: "text.secondary",
              }}
            >
              <Typography variant="body2">
                No messages yet. Start the conversation!
              </Typography>
            </Box>
          ) : (
            messages.flatMap((msg) => {
              // If message has segments, create a separate message box for each segment
              if (msg.segments && msg.segments.length > 0) {
                return msg.segments.map((segment, idx) => (
                  <Box
                    key={`${msg.id}_segment_${idx}`}
                    sx={{
                      display: "flex",
                      justifyContent:
                        msg.sender === "user" ? "flex-start" : "flex-end",
                      mb: 1,
                    }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        maxWidth: { xs: "88%", md: "72%" },
                        p: 1.5,
                        borderRadius: 2,
                        backgroundColor:
                          msg.sender === "user"
                            ? "action.hover"
                            : "primary.main",
                        color:
                          msg.sender === "user"
                            ? "text.primary"
                            : "primary.contrastText",
                      }}
                    >
                      {segment.type === "text" && segment.text && (
                        <Typography
                          variant="body2"
                          sx={{
                            wordBreak: "break-word",
                            textAlign: "left",
                          }}
                        >
                          {segment.text}
                        </Typography>
                      )}
                      {segment.type === "image" && segment.imageUrl && (
                        <Box>
                          <Box
                            component="img"
                            src={segment.imageUrl}
                            alt="message-image"
                            sx={{
                              maxWidth: "100%",
                              maxHeight: "300px",
                              borderRadius: 1,
                              marginBottom: segment.imageCaption ? 1 : 0,
                            }}
                          />
                          {segment.imageCaption && (
                            <Typography
                              variant="caption"
                              sx={{
                                wordBreak: "break-word",
                                display: "block",
                                textAlign: "left",
                              }}
                            >
                              {segment.imageCaption}
                            </Typography>
                          )}
                        </Box>
                      )}
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          mt: 0.5,
                          opacity: 0.7,
                          textAlign: msg.sender === "user" ? "right" : "left",
                        }}
                      >
                        {msg.timestamp.toLocaleTimeString("en-US", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </Typography>
                    </Paper>
                  </Box>
                ));
              } else {
                // No segments, render as single message box
                return (
                  <Box
                    key={msg.id}
                    sx={{
                      display: "flex",
                      justifyContent:
                        msg.sender === "agent" ? "flex-end" : "flex-start",
                      mb: 1,
                    }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        maxWidth: { xs: "88%", md: "72%" },
                        p: 1.5,
                        borderRadius: 2,
                        backgroundColor:
                          msg.sender === "agent"
                            ? "primary.main"
                            : "action.hover",
                        color:
                          msg.sender === "agent"
                            ? "primary.contrastText"
                            : "text.primary",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ wordBreak: "break-word" }}
                      >
                        {msg.text}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          mt: 0.5,
                          opacity: 0.7,
                        }}
                      >
                        {msg.timestamp.toLocaleTimeString("en-US", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </Typography>
                    </Paper>
                  </Box>
                );
              }
            })
          )}
          <div ref={messagesEndRef} />
        </Box>
      )}

      {/* Input Area */}
      <Box
        sx={{
          p: 2,
          borderTop: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
        }}
      >
        <Stack direction="row" spacing={1} alignItems="flex-end">
          {/* Template Button */}
          {/* <Tooltip title="Select a template">
            <IconButton
              onClick={handleOpenTemplateDialog}
              sx={{ color: "primary.main" }}
              size="small"
            >
              <TemplateIcon />
            </IconButton>
          </Tooltip> */}

          <TextField
            fullWidth
            multiline
            maxRows={4}
            placeholder="Type a message..."
            inputProps={{ "aria-label": "Message" }}
            value={messageInput}
            onChange={(e) => setMessageInput(e.target.value)}
            onKeyPress={handleKeyPress}
            variant="outlined"
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
          />

          <IconButton
            onClick={handleSend}
            aria-label="Send message"
            sx={{ color: "primary.main" }}
            size="small"
            loading={isSendingMessage}
            disabled={isSendingMessage}
          >
            <SendIcon />
          </IconButton>
        </Stack>
      </Box>

      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Conversation details</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <PersonIcon color="action" />
              <Typography variant="body2">{chat.phoneNumber}</Typography>
            </Stack>
            <FormControl fullWidth size="small">
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                Priority
              </Typography>
              <Select
                value={chat.priority}
                onChange={(event) =>
                  onUpdateMetadata({ priority: event.target.value as ConversationPriority })
                }
              >
                <MenuItem value="NORMAL">Normal</MenuItem>
                <MenuItem value="HIGH">High</MenuItem>
                <MenuItem value="URGENT">Urgent</MenuItem>
              </Select>
            </FormControl>
            <Divider />
            <TextField
              label="Assigned employee"
              value={assignedTo}
              onChange={(event) => setAssignedTo(event.target.value)}
              placeholder="Name or email"
              size="small"
            />
            <TextField
              label="Labels"
              value={labels}
              onChange={(event) => setLabels(event.target.value)}
              helperText="Separate labels with commas"
              size="small"
            />
            <TextField
              label="Internal notes"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              multiline
              minRows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              await onUpdateMetadata({
                assignedTo,
                labels: labels.split(",").map((label) => label.trim()).filter(Boolean),
                notes,
              });
              setDetailsOpen(false);
            }}
          >
            Save details
          </Button>
        </DialogActions>
      </Dialog>

      {/* Template Selection Dialog */}
      {/* <Dialog
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TemplateIcon />
          Select a Template
        </DialogTitle>
        <DialogContent dividers>
          {loadingTemplates ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                py: 3,
              }}
            >
              <CircularProgress size={40} />
            </Box>
          ) : templates.length === 0 ? (
            <Box sx={{ py: 3, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                No templates created yet. Create one in Settings &gt; WhatsApp
                Templates
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1}>
              {templates.map((template) => (
                <Card
                  key={template.id}
                  sx={{
                    cursor: "pointer",
                    transition: "background-color .15s ease",
                    border: "1px solid",
                    borderColor: "divider",
                    "&:hover": {
                      boxShadow: 2,
                      borderColor: "primary.main",
                      backgroundColor: "action.hover",
                    },
                  }}
                  onClick={() => handleSelectTemplate(template)}
                >
                  <CardContent sx={{ py: 1.5 }}>
                    <Stack spacing={1}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight={700}>
                          {template.name}
                        </Typography>
                        <Chip
                          label={template.status}
                          size="small"
                          color={
                            template.status === "APPROVED"
                              ? "success"
                              : "warning"
                          }
                          variant="outlined"
                        />
                      </Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {template.body}
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        {template.category} • {template.language}
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog> */}
    </Paper>
  );
};

export default function ChatInterface() {
  const { user } = useAuthUser();
  const [searchParams] = useSearchParams();
  const linkedPhone = searchParams.get("customerPhoneNumber") || "";
  const [agentSettings, setAgentSettings] =
    useState<Partial<AgentDetailModel>>();
  const [businessSettings, setBusinessSettings] = useState<BusinessSetting>();
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [selectedChat, setSelectedChat] = useState<ChatConversation | null>(
    null,
  );
  const [searchTerm, setSearchTerm] = useState(linkedPhone);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [inboxFilter, setInboxFilter] = useState<InboxFilter>("ALL");
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [conversationPage, setConversationPage] = useState(0);
  const [hasMoreConversations, setHasMoreConversations] = useState(false);
  const [totalConversations, setTotalConversations] = useState(0);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [messagePage, setMessagePage] = useState(0);
  const [hasOlderMessages, setHasOlderMessages] = useState(false);
  const [isTogglingAI, setIsTogglingAI] = useState(false);
  const [isSendingMessage, { open: sendingMessage, close: messageSent }] =
    useDisclosure(false);

  const chatReplays = useRef<any[]>([]);

  const fetchExistingSettings = useCallback(async () => {
    try {
      if (user?.uid) {
        const response = await getUserSettingsById(user.uid);

        if (response !== null) {
          const { agentSettings, businessSettings } = response;

          setAgentSettings({
            name: agentSettings.name,
            fallbackResponse: agentSettings.fallbackResponse,
            instructions: agentSettings.instructions,
            tones: agentSettings.tones,
            isActive: agentSettings.isActive,
          });

          setBusinessSettings({
            fullPhoneNumber: businessSettings.fullPhoneNumber,
            name: businessSettings.name,
            messageTemplate: businessSettings.messageTemplate,
            phoneNumberId: businessSettings.phoneNumberId,
            businessId: businessSettings.businessId,
          });

        } else {
          throw Error("User not logged in for fetching settings");
        }
      } else {
        throw new Error("Unable to get response for user settings");
      }
    } catch (e) {
      console.log(e);
      alert("Unable to fetch information right now. Please try again later");
    }
  }, [user?.uid]);

  const loadConversations = useCallback(async (page = 0, append = false) => {
    if (!businessSettings?.fullPhoneNumber) return;
    try {
      append ? setIsLoadingMore(true) : setIsLoading(true);
      const data = await getConversations(businessSettings.fullPhoneNumber, {
        page,
        size: 30,
        search: debouncedSearch,
        status: inboxFilter === "UNREAD" ? "ALL" : inboxFilter,
        unreadOnly: inboxFilter === "UNREAD",
      });

      const transformedConversations: ChatConversation[] = data.content.map((conv) => {
        const textContent = extractTextFromContent(conv.content);
        const segments = parseMessageSegments(conv.content);
        return {
          id: conv.fromPhoneNumber,
          name: conv.fromPhoneNumber || "Unknown Contact",
          phoneNumber: conv.fromPhoneNumber,
          lastMessage: textContent,
          lastMessageTime: new Date(conv.createdOn),
          unreadCount: conv.unreadCount,
          crmStatus: conv.status || "OPEN",
          priority: conv.priority || "NORMAL",
          assignedTo: conv.assignedTo,
          labels: conv.labels || [],
          notes: conv.notes,
          markedUnread: conv.markedUnread,
          messages: [
            {
              id: conv.id,
              text: textContent,
              segments,
              sender: conv.role === "user" ? "user" : "agent",
              timestamp: new Date(conv.createdOn),
            },
          ],
        };
      });

      setConversations((current) => {
        if (!append) return transformedConversations;
        const combined = new Map(
          [...current, ...transformedConversations].map((conversation) => [
            conversation.phoneNumber,
            conversation,
          ]),
        );
        return Array.from(combined.values());
      });
      setConversationPage(data.page);
      setHasMoreConversations(data.hasNext);
      setTotalConversations(data.totalElements);
    } catch (err) {
      console.error("Error loading conversations:", err);
      if (!append) setConversations([]);
    } finally {
      append ? setIsLoadingMore(false) : setIsLoading(false);
    }
  }, [businessSettings?.fullPhoneNumber, debouncedSearch, inboxFilter]);

  useEffect(() => {
    fetchExistingSettings();
  }, [fetchExistingSettings]);

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedSearch(searchTerm), 350);
    return () => window.clearTimeout(timeout);
  }, [searchTerm]);

  useEffect(() => {
    if (businessSettings?.fullPhoneNumber) {
      loadConversations(0, false);
    }
  }, [businessSettings?.fullPhoneNumber, debouncedSearch, inboxFilter, loadConversations]);

  const transformMessages = (messageData: any[]): ChatMessage[] => {
    const messages: ChatMessage[] = [];
    messageData.forEach((msg) => {
      const concatenatedMessages = parseConcatenatedMessages(
        typeof msg.content === "string" ? msg.content : JSON.stringify(msg.content),
      );
      if (concatenatedMessages) {
        concatenatedMessages.forEach((parsedMsg) => {
          messages.push({
            ...parsedMsg,
            id: `${msg.id}_${messages.length}`,
            sender: msg.role === "user" ? "user" : "agent",
            timestamp: new Date(msg.createdOn),
          });
        });
      } else {
        messages.push({
          id: msg.id,
          text: extractTextFromContent(msg.content),
          segments: parseMessageSegments(msg.content),
          sender: msg.role === "user" ? "user" : "agent",
          timestamp: new Date(msg.createdOn),
        });
      }
    });
    return messages;
  };

  const loadConversationMessages = async (
    chat: ChatConversation,
    page = 0,
    prepend = false,
    mergeLatest = false,
  ) => {
    if (!businessSettings?.fullPhoneNumber || !chat.phoneNumber) return;
    try {
      prepend ? setIsLoadingOlder(true) : !mergeLatest && setIsLoadingMessages(true);
      const response = await getConversationMessages(
        businessSettings.fullPhoneNumber,
        chat.phoneNumber,
        page,
        50,
      );
      const messages = transformMessages(response.content);
      setSelectedChat((current) => {
        if (!current || current.phoneNumber !== chat.phoneNumber) return current;
        if (!prepend && !mergeLatest) return { ...current, messages };
        const combined = new Map(
          [...(prepend ? messages : current.messages), ...(prepend ? current.messages : messages)].map(
            (message) => [message.id, message],
          ),
        );
        return {
          ...current,
          unreadCount: 0,
          markedUnread: false,
          messages: Array.from(combined.values()).sort(
            (left, right) => left.timestamp.getTime() - right.timestamp.getTime(),
          ),
        };
      });
      setConversations((current) =>
        current.map((conversation) =>
          conversation.phoneNumber === chat.phoneNumber
            ? { ...conversation, unreadCount: 0, markedUnread: false }
            : conversation,
        ),
      );
      setMessagePage(page);
      setHasOlderMessages(response.hasNext);
    } catch (err) {
      console.error("Error loading conversation messages:", err);
      if (!mergeLatest) {
        alert("Unable to load conversation messages. Please try again later.");
      }
    } finally {
      setIsLoadingMessages(false);
      setIsLoadingOlder(false);
    }
  };

  const clearChatRefresh = () => {
    chatReplays.current?.forEach((cr) => {
      if (cr) clearInterval(cr);
    });
    chatReplays.current = [];
  };

  useEffect(() => clearChatRefresh, []);

  const handleSelectChat = (chat: ChatConversation) => {
    clearChatRefresh();

    setSelectedChat(chat);
    setMessagePage(0);
    setHasOlderMessages(false);
    loadConversationMessages(chat, 0);
    const configuredRefresh = Number(process.env.REACT_APP_CHAT_REFRESH_TIME);
    const refreshMilliseconds =
      Number.isFinite(configuredRefresh) && configuredRefresh >= 5000
        ? configuredRefresh
        : 10000;
    chatReplays.current.push(
      setInterval(() => loadConversationMessages(chat, 0, false, true), refreshMilliseconds),
    );
  };

  useEffect(() => {
    if (!businessSettings?.fullPhoneNumber || !/^\d{8,15}$/.test(linkedPhone)) return;
    setSearchTerm(linkedPhone);
    // Open directly even when this customer's conversation is outside the first inbox page.
    handleSelectChat({ id: linkedPhone, phoneNumber: linkedPhone, name: linkedPhone,
      lastMessage: "", lastMessageTime: new Date(), unreadCount: 0, crmStatus: "OPEN",
      priority: "NORMAL", labels: [], markedUnread: false, messages: [] });
    // The link should open once when its target/business changes, not on every message refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedPhone, businessSettings?.fullPhoneNumber]);

  const handleUpdateMetadata = async (update: ConversationMetadataUpdate) => {
    if (!selectedChat?.phoneNumber || !businessSettings?.fullPhoneNumber) return;
    await updateConversationMetadata(
      businessSettings.fullPhoneNumber,
      selectedChat.phoneNumber,
      update,
    );
    const applyUpdate = (chat: ChatConversation): ChatConversation => ({
      ...chat,
      ...(update.status ? { crmStatus: update.status } : {}),
      ...(update.priority ? { priority: update.priority } : {}),
      ...(update.assignedTo !== undefined ? { assignedTo: update.assignedTo } : {}),
      ...(update.labels ? { labels: update.labels } : {}),
      ...(update.notes !== undefined ? { notes: update.notes } : {}),
      ...(update.markedUnread !== undefined
        ? {
            markedUnread: update.markedUnread,
            unreadCount: update.markedUnread ? Math.max(1, chat.unreadCount) : 0,
          }
        : {}),
    });
    setSelectedChat((current) => (current ? applyUpdate(current) : current));
    setConversations((current) =>
      current.map((chat) =>
        chat.phoneNumber === selectedChat.phoneNumber ? applyUpdate(chat) : chat,
      ),
    );
  };

  const handleSendMessage = async (messageText: any) => {
    if (!selectedChat) return;

    try {
      // Send message via WhatsApp API (use phoneNumber if available, otherwise use id as fallback)
      // const phoneNumber = selectedChat.phoneNumber || selectedChat.id;
      sendingMessage();
      await sendMessage(user?.uid!, selectedChat.phoneNumber!, messageText);

      const newMessage: ChatMessage = {
        id: `m${Date.now()}`,
        text: messageText,
        sender: "agent",
        timestamp: new Date(),
      };

      // Update selected chat
      const updatedChat = {
        ...selectedChat,
        messages: [...selectedChat.messages, newMessage],
        lastMessage: messageText,
        lastMessageTime: new Date(),
      };

      setSelectedChat(updatedChat);

      // Update conversations list
      setConversations((current) =>
        current.map((c) => (c.phoneNumber === updatedChat.phoneNumber ? updatedChat : c)),
      );
    } catch (err) {
      console.error("Error sending message:", err);
      alert(
        "Unable to send messages right now, Please try again in sometime or contact the admin",
      );
    } finally {
      messageSent();
    }
  };

  const toggleAIStatus = async (isActive: boolean) => {
    try {
      setIsTogglingAI(true);
      await updateAgentStatus(user?.uid!, isActive);
      setAgentSettings((prev) => ({ ...prev, isActive: isActive }));
    } catch (e) {
      alert(`Unable to turn AI ${isActive ? "On" : "Off"}. Please try again.`);
      console.log(e);
    } finally {
      setIsTogglingAI(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        minWidth: 0,
        backgroundColor: "background.default",
      }}
    >
      {/* Global Header with AI Toggle */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
        }}
      >
        <Typography variant="h6" fontWeight={700}>
          Conversations
        </Typography>
        <Tooltip title={agentSettings?.isActive ? "AI Enabled" : "AI Disabled"}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              // backgroundColor: agentSettings?.isActive
              //   ? "primary.light"
              //   : "action.hover",
            }}
          >
            <AIIcon
              fontSize="small"
              sx={{
                color: agentSettings?.isActive ? "success.main" : "text.secondary",
              }}
            />
            {isTogglingAI ? (
              <CircularProgress size={24} sx={{ color: "success.main" }} />
            ) : (
              <Switch
                size="small"
                checked={agentSettings?.isActive}
                onChange={(e) => toggleAIStatus(e.target.checked)}
                disabled={isTogglingAI}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "success.main",
                  },
                }}
              />
            )}
          </Box>
        </Tooltip>
      </Box>

      {/* Main Chat Area */}
      <Box
        sx={{
          display: "flex",
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* Left Sidebar - Chat List */}
        <Box
          sx={{
            width: { xs: selectedChat ? 0 : "100%", sm: 300, xl: 340 },
            display: { xs: selectedChat ? "none" : "flex", sm: "flex" },
            flexDirection: "column",
            flexShrink: 0,
            minWidth: 0,
          }}
        >
          <ChatList
            conversations={conversations}
            selectedChat={selectedChat}
            onSelectChat={handleSelectChat}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            isLoading={isLoading}
            isLoadingMore={isLoadingMore}
            hasMore={hasMoreConversations}
            totalConversations={totalConversations}
            filter={inboxFilter}
            onFilterChange={setInboxFilter}
            onLoadMore={() => loadConversations(conversationPage + 1, true)}
          />
        </Box>

        {/* Right Side - Chat Panel */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: { xs: selectedChat ? "flex" : "none", sm: "flex" },
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          {selectedChat && (
            <Box
              sx={{
                display: { xs: "flex", sm: "none" },
                alignItems: "center",
                p: 1,
                borderBottom: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.paper",
              }}
            >
              <IconButton
                onClick={() => setSelectedChat(null)}
                size="small"
                sx={{ mr: 1 }}
              >
                <CloseIcon />
              </IconButton>
              <Typography variant="subtitle2" fontWeight={600}>
                Back
              </Typography>
            </Box>
          )}

          <ChatPanel
            isSendingMessage={isSendingMessage}
            chat={selectedChat}
            onSendMessage={handleSendMessage}
            messages={selectedChat?.messages || []}
            isLoadingMessages={isLoadingMessages}
            isLoadingOlder={isLoadingOlder}
            hasOlderMessages={hasOlderMessages}
            onLoadOlder={() =>
              selectedChat &&
              loadConversationMessages(selectedChat, messagePage + 1, true)
            }
            onUpdateMetadata={handleUpdateMetadata}
          />
        </Box>
      </Box>
    </Box>
  );
}
